import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import useSound from 'use-sound';
import { useReactToPrint } from 'react-to-print';
import ReceivingLabelPrint from '../ReceivingLabelPrint';
import * as qzPrint from '../../utils/qzPrint';
import './GoodsReceipt.css';

/**
 * Goods Receipt Page
 * Smart receiving system with barcode scanning, vendor selection, and touch screen interface
 */

function GoodsReceipt() {
  // Sound effects
  const [playSuccess] = useSound('/sounds/success.mp3', { volume: 0.5 });
  const [playError] = useSound('/sounds/error.mp3', { volume: 0.5 });
  const [playComplete] = useSound('/sounds/complete.mp3', { volume: 0.5 });

  // Operator state
  const [currentOperator, setCurrentOperator] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPinEntry, setShowPinEntry] = useState(true);

  // Main state
  const [mode, setMode] = useState('po-selection'); // po-selection, receiving, completed
  const [likelyPOs, setLikelyPOs] = useState([]);
  const [poFilter, setPoFilter] = useState('');
  const [poSort, setPoSort] = useState({ key: 'order_date', dir: 'desc' });
  // Persisted per-station so a store kiosk can be set to "Candlefish" once
  // and stay that way, while the warehouse keeps seeing everything.
  const [subsidiaryFilter, setSubsidiaryFilter] = useState(() => (
    localStorage.getItem('goodsReceipt.subsidiaryFilter') || 'all'
  ));
  useEffect(() => {
    localStorage.setItem('goodsReceipt.subsidiaryFilter', subsidiaryFilter);
  }, [subsidiaryFilter]);
  // po.id currently running a Receive All / Void action, or null
  const [poActionId, setPoActionId] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [openPOs, setOpenPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [receivedItems, setReceivedItems] = useState([]);
  const [selectedLineItems, setSelectedLineItems] = useState([]); // Multi-select
  const [receiveQueue, setReceiveQueue] = useState([]); // Queue for batch receiving

  // Auto-print settings with localStorage persistence
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
    const saved = localStorage.getItem('goodsReceipt.autoPrintEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [skipLabelPrompt, setSkipLabelPrompt] = useState(() => {
    const saved = localStorage.getItem('goodsReceipt.skipLabelPrompt');
    return saved !== null ? saved === 'true' : true;
  });
  const [defaultLabelCount, setDefaultLabelCount] = useState(() => {
    const saved = localStorage.getItem('goodsReceipt.defaultLabelCount');
    return saved ? parseInt(saved) : 1;
  });

  // ZPL printing settings - QZ Tray only (hardcoded for simplicity)
  const [printerIp, setPrinterIp] = useState(() => {
    return localStorage.getItem('goodsReceipt.printerIp') || '';
  });
  const printMethod = 'qz'; // Hardcoded to QZ Tray - no picker needed
  const [qzConnected, setQzConnected] = useState(false);
  const [qzPrinters, setQzPrinters] = useState([]);
  const [qzSelectedPrinter, setQzSelectedPrinter] = useState(''); // Auto-detect Zebra
  const [localPrintServer, setLocalPrintServer] = useState(() => {
    return localStorage.getItem('goodsReceipt.localPrintServer') || 'http://localhost:9100';
  });
  const [cloudStationId, setCloudStationId] = useState(() => {
    return localStorage.getItem('goodsReceipt.cloudStationId') || '';
  });
  const [cloudStationStatus, setCloudStationStatus] = useState(null);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(() => {
    return localStorage.getItem('goodsReceipt.selectedPrinter') || '';
  });
  const [showPrinterSetup, setShowPrinterSetup] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [testPrintStatus, setTestPrintStatus] = useState(null);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('goodsReceipt.autoPrintEnabled', autoPrintEnabled);
  }, [autoPrintEnabled]);

  useEffect(() => {
    localStorage.setItem('goodsReceipt.skipLabelPrompt', skipLabelPrompt);
  }, [skipLabelPrompt]);

  useEffect(() => {
    localStorage.setItem('goodsReceipt.defaultLabelCount', defaultLabelCount);
  }, [defaultLabelCount]);

  useEffect(() => {
    localStorage.setItem('goodsReceipt.printerIp', printerIp);
  }, [printerIp]);

  // Connect to QZ Tray on mount
  useEffect(() => {
    connectToQZ();
  }, []);

  const connectToQZ = async () => {
    try {
      await qzPrint.setStation('pack');
      await qzPrint.connect();
      setQzConnected(true);
      const printers = await qzPrint.getPrinters();
      setQzPrinters(printers);

      // Auto-select Zebra printer
      const zebra = printers.find(p => p.toLowerCase().includes('zebra'));
      if (zebra) {
        setQzSelectedPrinter(zebra);
        console.log('QZ Tray: Auto-selected Zebra printer:', zebra);
      }
    } catch (err) {
      console.error('QZ Tray connection failed:', err);
      setQzConnected(false);
      // Don't show error on initial load - just log it
      console.log('QZ Tray not available - will retry when printing');
    }
  };

  // Check cloud print station status periodically
  useEffect(() => {
    if (printMethod === 'cloud' && cloudStationId) {
      checkCloudStationStatus();
      const interval = setInterval(checkCloudStationStatus, 30000); // Check every 30s
      return () => clearInterval(interval);
    }
  }, [printMethod, cloudStationId]);

  const checkCloudStationStatus = async () => {
    if (!cloudStationId) return;
    try {
      const response = await axios.get(`/api/print/cloud/station/${cloudStationId}`);
      setCloudStationStatus(response.data);
    } catch (err) {
      setCloudStationStatus({ status: 'unknown', error: err.message });
    }
  };

  // UI state
  const [scanInput, setScanInput] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Quantity editor state
  const [editingItem, setEditingItem] = useState(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [showKeypad, setShowKeypad] = useState(false);
  const [userStartedTyping, setUserStartedTyping] = useState(false);

  // Search keypad state
  const [showSearchKeypad, setShowSearchKeypad] = useState(false);
  const [searchKeypadValue, setSearchKeypadValue] = useState('');

  // Label printing state
  const [showLabelPrompt, setShowLabelPrompt] = useState(false);
  const [labelQuantity, setLabelQuantity] = useState(1);
  const [labelItem, setLabelItem] = useState(null);
  const [pendingReceiveData, setPendingReceiveData] = useState(null);
  const [readyToPrint, setReadyToPrint] = useState(false);
  const [printData, setPrintData] = useState(null);

  // Refs
  const scanInputRef = useRef(null);
  const vendorSearchRef = useRef(null);
  const labelPrintRef = useRef(null);

  // Load likely POs and printer settings on mount
  useEffect(() => {
    loadLikelyPOs();
    loadPrinterSettings();
    // Only load printers if using server-based printing
    if (printMethod === 'usb') {
      loadAvailablePrinters();
    }
  }, []);

  // Verify operator PIN
  const verifyPin = async () => {
    if (pinInput.length !== 4) {
      setPinError('PIN must be 4 digits');
      return;
    }

    try {
      const response = await axios.post('/api/operators/pin', { pin: pinInput });
      if (response.data && response.data.id) {
        setCurrentOperator(response.data);
        setShowPinEntry(false);
        setPinInput('');
        setPinError('');
        playSuccess();
      } else {
        setPinError('Invalid PIN');
        playError();
      }
    } catch (err) {
      console.error('PIN verification error:', err);
      setPinError(err.response?.data?.error || 'Invalid PIN');
      playError();
    }
  };

  // Handle PIN keypad press
  const handlePinKeypadPress = (key) => {
    if (key === '←') {
      setPinInput(prev => prev.slice(0, -1));
    } else if (key === 'C') {
      setPinInput('');
    } else if (pinInput.length < 4) {
      setPinInput(prev => prev + key);
    }
  };

  // Logout operator
  const logoutOperator = () => {
    setCurrentOperator(null);
    setShowPinEntry(true);
    setPinInput('');
    resetReceiving();
  };

  // Get operator display name
  const getOperatorName = () => {
    if (!currentOperator) return '';
    return currentOperator.display_name ||
           `${currentOperator.first_name || ''} ${currentOperator.last_name || ''}`.trim() ||
           'Operator';
  };

  // Load printer settings from server
  const loadPrinterSettings = async () => {
    try {
      const response = await axios.get('/api/print/settings');
      if (response.data.printerIp && !printerIp) {
        setPrinterIp(response.data.printerIp);
      }
      // printMethod is hardcoded to 'qz' - no need to load from server
    } catch (err) {
      console.log('No server printer settings found, using local settings');
    }
  };

  // Load available printers from system
  const loadAvailablePrinters = async () => {
    try {
      const response = await axios.get('/api/print/printers');
      if (response.data.success && response.data.printers) {
        setAvailablePrinters(response.data.printers);

        // Auto-select printer (prioritize USB001, then any Zebra)
        if (!selectedPrinter) {
          const usb001Printer = response.data.printers.find(p =>
            p.port && (p.port.toUpperCase() === 'USB001' || p.port.toUpperCase().includes('USB001'))
          );

          if (usb001Printer) {
            setSelectedPrinter(usb001Printer.name);
          } else {
            const zebraPrinter = response.data.printers.find(p => p.isZebra);
            if (zebraPrinter) {
              setSelectedPrinter(zebraPrinter.name);
            }
          }
        }
      }
      return response.data;
    } catch (err) {
      console.log('Could not load printers:', err);
      return { success: false, error: err.message };
    }
  };

  // Run printer diagnostics
  const runDiagnostics = async () => {
    setDiagnosticResults(null);
    setTestPrintStatus(null);

    const results = {
      timestamp: new Date().toISOString(),
      printers: [],
      usbPrinters: [],
      zebraPrinters: [],
      usb001: null,
      recommendations: [],
      errors: [],
      printMethod: printMethod,
      localServerStatus: null
    };

    // Check if using local print server
    if (printMethod === 'local') {
      try {
        const healthCheck = await axios.get(`${localPrintServer}/health`);
        results.localServerStatus = healthCheck.data;

        // Try to get printers from local server
        const printersResponse = await axios.get(`${localPrintServer}/printers`);
        if (printersResponse.data.printers) {
          results.printers = printersResponse.data.printers;
          results.recommendations.push({
            type: 'success',
            text: `Local print server connected! ${printersResponse.data.zebraFound ? 'Zebra printer detected.' : 'No Zebra printer found.'}`
          });
        }
      } catch (err) {
        results.errors.push('Local print server not running');
        results.recommendations.push({
          type: 'error',
          text: 'Local print server not running. Please run "run-print-server.bat" on the workstation with the printer.'
        });
      }

      setDiagnosticResults(results);
      return;
    }

    // Load printers from server (for USB mode)
    const printerData = await loadAvailablePrinters();

    if (printerData.success) {
      results.printers = printerData.printers || [];

      // Find USB printers
      results.usbPrinters = results.printers.filter(p =>
        p.port && p.port.toUpperCase().includes('USB')
      );

      // Find Zebra printers
      results.zebraPrinters = results.printers.filter(p => p.isZebra);

      // Check USB001 - handle both exact match and contains
      results.usb001 = results.printers.find(p =>
        p.port && (
          p.port.toUpperCase() === 'USB001' ||
          p.port.toUpperCase().includes('USB001') ||
          p.port.replace(/\s/g, '').toUpperCase() === 'USB001'
        )
      );

      // Generate recommendations
      if (results.usb001 && results.usb001.isZebra) {
        results.recommendations.push({
          type: 'success',
          text: `Perfect! Use "${results.usb001.name}" on USB001 for best results.`
        });
      } else if (results.zebraPrinters.length > 0 && results.usbPrinters.length > 0) {
        const bestPrinter = results.zebraPrinters.find(p =>
          p.port && p.port.toUpperCase().includes('USB')
        );
        if (bestPrinter) {
          results.recommendations.push({
            type: 'success',
            text: `Found Zebra USB printer: "${bestPrinter.name}" on ${bestPrinter.port}`
          });
        }
      } else if (results.usbPrinters.length > 0) {
        results.recommendations.push({
          type: 'warning',
          text: `USB printer found but not Zebra: "${results.usbPrinters[0].name}". May work with limitations.`
        });
      } else if (results.zebraPrinters.length > 0) {
        results.recommendations.push({
          type: 'warning',
          text: `Zebra printer found but not on USB: "${results.zebraPrinters[0].name}". Try Network printing.`
        });
      } else if (results.printers.length === 0) {
        results.recommendations.push({
          type: 'error',
          text: 'No printers detected! Check USB connection and drivers.'
        });
      } else {
        results.recommendations.push({
          type: 'error',
          text: 'No suitable label printer found. Connect a Zebra printer via USB.'
        });
      }
    } else {
      results.errors.push(printerData.error || 'Failed to load printers');
    }

    setDiagnosticResults(results);
  };

  // Send test print
  const sendTestPrint = async () => {
    setTestPrintStatus({ loading: true, message: 'Sending test label...' });

    try {
      const testItem = {
        item_id: 'TEST001',
        item_sku: 'DIAGNOSTIC-TEST',
        display_name: 'Printer Test Label',
        vendor_name: 'Diagnostic Tool',
        poLine: {
          item_id: 'TEST001',
          item_sku: 'DIAGNOSTIC-TEST',
          vendor_name: 'Diagnostic Tool'
        }
      };

      // Check if local print server is selected and available
      if (printMethod === 'local') {
        try {
          // Test server connection first
          const healthCheck = await axios.get(`${localPrintServer}/health`);
          if (!healthCheck.data.status) {
            setTestPrintStatus({
              success: false,
              message: 'Local print server not responding. Please start the print server on the workstation.'
            });
            return;
          }
        } catch (err) {
          setTestPrintStatus({
            success: false,
            message: 'Local print server not running. Please run "run-print-server.bat" on the workstation with the printer.'
          });
          return;
        }
      }

      const success = await printZPLLabels(
        testItem,
        'TEST-' + Date.now().toString().slice(-6),
        new Date(),
        1
      );

      if (success) {
        setTestPrintStatus({
          success: true,
          message: 'Test label sent successfully! Check your printer.'
        });
      } else {
        setTestPrintStatus({
          success: false,
          message: 'Test print failed. Check diagnostics above.'
        });
      }
    } catch (error) {
      setTestPrintStatus({
        success: false,
        message: `Test failed: ${error.message}`
      });
    }
  };

  // Reset function after printing
  const resetAfterPrint = () => {
    setShowLabelPrompt(false);
    setPendingReceiveData(null);
    setLabelItem(null);
    setError(null);

    // If more items in queue, process next
    if (receiveQueue.length > 1) {
      continueToNextItem();
    } else {
      setEditingItem(null);
      setQuantityInput('');
      setSelectedLineItems([]);
      setReceiveQueue([]);
      // Focus back on scan input
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
          scanInputRef.current.select();
        }
      }, 100);
    }
  };

  // ZPL Print function
  const printZPLLabels = async (item, poNumber, receivedDate, quantity, poInternalId = null) => {
    try {
      if (printMethod === 'qz') {
        // QZ Tray - silent printing via WebSocket to local QZ Tray
        try {
          // Generate ZPL on backend
          const zplResponse = await axios.post('/api/print/generate-zpl', {
            item,
            poNumber,
            receivedDate,
            quantity,
            poInternalId,
            receivedBy: getOperatorName()
          });

          if (!zplResponse.data.zpl) {
            throw new Error('Failed to generate ZPL');
          }

          // Print via QZ Tray
          const result = await qzPrint.printZPL(zplResponse.data.zpl, qzSelectedPrinter || null);

          playSuccess();
          setMessage(`Printed ${quantity} label${quantity > 1 ? 's' : ''} to ${result.printer}`);
          return true;
        } catch (error) {
          console.error('QZ Tray print error:', error);
          if (error.message?.includes('not running') || error.message?.includes('WebSocket')) {
            setError('QZ Tray not running. Please start QZ Tray on the workstation.');
          } else {
            setError(`QZ print failed: ${error.message}`);
          }
          return false;
        }
      } else if (printMethod === 'cloud') {
        // Cloud print queue - sends job to Supabase for local agent to pick up
        if (!cloudStationId) {
          setError('Please enter a Print Station ID for cloud printing');
          return false;
        }

        try {
          // Generate ZPL on backend
          const zplResponse = await axios.post('/api/print/generate-zpl', {
            item,
            poNumber,
            receivedDate,
            quantity,
            poInternalId,
            receivedBy: getOperatorName()
          });

          if (!zplResponse.data.zpl) {
            throw new Error('Failed to generate ZPL');
          }

          // Send to cloud print queue
          const queueResponse = await axios.post('/api/print/cloud/queue', {
            stationId: cloudStationId,
            jobType: 'receiving_label',
            zplData: zplResponse.data.zpl,
            metadata: {
              poNumber,
              itemSku: item.item_sku || item.sku,
              itemName: item.display_name,
              quantity
            }
          });

          if (queueResponse.data.success) {
            playSuccess();
            setMessage(`Label queued for printing (Job #${queueResponse.data.jobId?.slice(-6) || 'pending'})`);
            return true;
          } else {
            throw new Error(queueResponse.data.error || 'Failed to queue print job');
          }
        } catch (error) {
          console.error('Cloud print queue error:', error);
          setError(`Cloud print failed: ${error.response?.data?.error || error.message}`);
          return false;
        }
      } else if (printMethod === 'local') {
        // Local print server - for USB printers on client workstation
        try {
          // First, generate ZPL on our backend
          const zplResponse = await axios.post('/api/print/generate-zpl', {
            item,
            poNumber,
            receivedDate,
            quantity,
            poInternalId,
            receivedBy: getOperatorName()
          });

          if (zplResponse.data.zpl) {
            console.log('Generated ZPL length:', zplResponse.data.zpl.length);
            console.log('First 100 chars of ZPL:', zplResponse.data.zpl.substring(0, 100));

            // Validate ZPL starts with ^XA (ZPL start command)
            if (!zplResponse.data.zpl.startsWith('^XA')) {
              throw new Error('Invalid ZPL generated - does not start with ^XA');
            }

            // Send ZPL to local print server
            const printResponse = await axios.post(`${localPrintServer}/print`, {
              zpl: zplResponse.data.zpl,
              printerName: selectedPrinter || null // null will auto-detect
            });

            if (printResponse.data.success) {
              playSuccess();
              setMessage(`Sent ${quantity} label${quantity > 1 ? 's' : ''} to local printer`);
              return true;
            } else {
              throw new Error(printResponse.data.error || 'Local print failed');
            }
          } else {
            throw new Error('Failed to generate ZPL');
          }
        } catch (error) {
          console.error('Local print error:', error);
          console.error('Error response:', error.response?.data);

          if (error.code === 'ERR_NETWORK') {
            setError('Local print server not running. Please start the print server on the workstation.');
          } else if (error.response?.data?.error) {
            setError(`Local print failed: ${error.response.data.error}`);
          } else {
            setError(`Local print failed: ${error.message}`);
          }
          return false;
        }
      } else if (printMethod === 'usb') {
        // USB printing - auto-detect or use selected printer
        const printerInfo = availablePrinters.find(p => p.name === selectedPrinter);

        // Check if we have printers available
        if (availablePrinters.length === 0) {
          await loadAvailablePrinters();
        }

        const response = await axios.post('/api/print/usb', {
          printerName: selectedPrinter || undefined,
          printerPort: printerInfo?.port || undefined,
          item,
          poNumber,
          receivedDate,
          quantity
        });

        if (response.data.success) {
          playSuccess();
          setMessage(`Sent ${quantity} label${quantity > 1 ? 's' : ''} to USB printer`);
          return true;
        } else {
          console.error('USB print error:', response.data.error);
          setError(`USB print failed: ${response.data.error || 'Unknown error'}`);
          return false;
        }
      } else if (printMethod === 'network' && printerIp) {
        // Send ZPL directly to network printer
        const response = await axios.post('/api/print/network', {
          printerIp,
          printerPort: 9100,
          item,
          poNumber,
          receivedDate,
          quantity
        });

        if (response.data.success) {
          playSuccess();
          setMessage(`Sent ${quantity} label${quantity > 1 ? 's' : ''} to printer`);
          return true;
        } else {
          throw new Error(response.data.error || 'Print failed');
        }
      } else if (printMethod === 'download') {
        // Download ZPL file
        const response = await axios.post('/api/print/download-zpl', {
          item,
          poNumber,
          receivedDate,
          quantity
        }, {
          responseType: 'blob'
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `label_${poNumber}_${Date.now()}.zpl`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        playSuccess();
        setMessage(`Downloaded ZPL file with ${quantity} label${quantity > 1 ? 's' : ''}`);
        return true;
      } else {
        // For other methods (browser), return false to use fallback
        return false;
      }
    } catch (error) {
      console.error('ZPL print error:', error);
      setError(`Print failed: ${error.response?.data?.error || error.message}`);
      return false;
    }
  };

  // Print handler optimized for Zebra label printer
  const handlePrint = useReactToPrint({
    content: () => labelPrintRef.current,
    pageStyle: `
      @page {
        size: 4in 3in;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `,
    documentTitle: `Label_${new Date().getTime()}`,
    onBeforeGetContent: () => {
      // Ensure content is ready
      return new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    },
    print: async (printIframe) => {
      // Custom print function for silent printing
      const printWindow = printIframe.contentWindow;

      // Try to use Chrome's silent print if available
      if (printWindow.chrome && printWindow.chrome.printing) {
        try {
          // Chrome printing API (requires extension or enterprise policy)
          await printWindow.chrome.printing.print({
            silent: true,
            printBackground: true
          });
          return;
        } catch (e) {
          console.log('Chrome printing API not available, using standard print');
        }
      }

      // Fallback to standard print
      try {
        printWindow.focus();
        printWindow.print();
      } catch (e) {
        console.error('Print failed:', e);
      }
    },
    onAfterPrint: () => {
      console.log('Label printed successfully');
      setReadyToPrint(false);
      setPrintData(null);

      // Reset after printing
      setEditingItem(null);
      setQuantityInput('');
      setShowLabelPrompt(false);
      setPendingReceiveData(null);
      setLabelItem(null);
      setError(null);

      // Immediate focus back on scan input for next item
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
          scanInputRef.current.select();
        }
      }, 50);
    }
  });

  // Trigger print when ready
  useEffect(() => {
    if (readyToPrint && labelPrintRef.current) {
      handlePrint();
    }
  }, [readyToPrint, handlePrint]);

  // Auto-focus scan input when in receiving mode
  useEffect(() => {
    if (mode === 'receiving' && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [mode]);

  // Load all open POs to receive (last 6 months, both Rewined and Candlefish)
  const loadLikelyPOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/goods-receipt/likely-pos');
      setLikelyPOs(response.data);
    } catch (err) {
      console.error('Error loading likely POs:', err);
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  // Search vendors by name
  const searchVendors = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get('/api/goods-receipt/pos/search', {
        params: { query }
      });
      setSearchResults(response.data);
    } catch (err) {
      console.error('Error searching POs:', err);
      setSearchResults([]);
    }
  };

  // Debounced PO search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (vendorSearch) {
        searchVendors(vendorSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearch]);

  // Select a vendor
  const selectVendor = async (vendor) => {
    try {
      setLoading(true);
      setSelectedVendor(vendor);
      setVendorSearch('');
      setSearchResults([]);

      // Load open POs for this vendor
      const response = await axios.get(`/api/goods-receipt/vendors/${vendor.id}/pos`);
      setOpenPOs(response.data);

      // If only one PO, auto-select it
      if (response.data.length === 1) {
        selectPO(response.data[0]);
      } else if (response.data.length === 0) {
        setError('No open purchase orders found for this vendor');
        setSelectedVendor(null);
      } else {
        setMode('po-selection');
      }
    } catch (err) {
      console.error('Error loading POs:', err);
      setError('Failed to load purchase orders');
      setSelectedVendor(null);
    } finally {
      setLoading(false);
    }
  };

  // Select a purchase order
  const selectPO = async (po) => {
    try {
      setLoading(true);

      // Use the PO data from the list (already cached)
      // Fresh fetch disabled temporarily - using 15-min cache instead
      setSelectedPO(po);

      // Create a new goods receipt
      const response = await axios.post('/api/goods-receipt/receipt', {
        po_id: po.id,
        po_number: po.po_number,
        vendor_id: po.vendor_id,
        vendor_name: po.vendor_name,
        received_by: getOperatorName()
      });

      setCurrentReceipt(response.data);
      setMode('receiving');
      setMessage(`Receiving PO ${po.po_number}`);
    } catch (err) {
      console.error('Error starting receiving:', err);
      if (err.response?.status === 404) {
        setError('PO not found - may be fully received or closed');
      } else {
        setError(err.response?.data?.error || 'Failed to start receiving process');
      }
    } finally {
      setLoading(false);
    }
  };

  // Receive every remaining line on a PO in one shot — for when a shipment
  // arrived complete and matches the PO, no scanning needed.
  const handleReceiveAll = async (po) => {
    if (!window.confirm(`Receive all remaining items on PO ${po.po_number} and submit to NetSuite?`)) return;
    setPoActionId(po.id);
    setError(null);
    try {
      const response = await axios.post(`/api/goods-receipt/po/${po.id}/receive-all`, {
        received_by: getOperatorName()
      });
      const itemReceiptNumber = response.data?.netsuite_item_receipt_number;
      setMessage(
        `PO ${po.po_number} fully received`
        + (itemReceiptNumber ? ` — NetSuite Item Receipt ${itemReceiptNumber}` : '')
        + (!itemReceiptNumber && response.data?.native ? ' natively — NetSuite is unreachable and has no item receipt for it' : '')
      );
      playComplete();
      setLikelyPOs(prev => prev.filter(p => p.id !== po.id));
    } catch (err) {
      console.error('Error receiving all:', err);
      playError();
      setError(err.response?.data?.error || 'Failed to receive all items');
    } finally {
      setPoActionId(null);
    }
  };

  // Void a PO that will never be received — closes it out in NetSuite so it
  // drops off this list.
  const handleVoidPO = async (po) => {
    const { received } = getPOProgress(po);
    const confirmMsg = received > 0
      ? `Close out the remaining unreceived items on PO ${po.po_number}? What's already been received stays as-is — this just marks the rest as never coming, so the PO drops off this list. Cannot be undone.`
      : `Void PO ${po.po_number}? This closes it out in NetSuite and cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setPoActionId(po.id);
    setError(null);
    try {
      await axios.post(`/api/goods-receipt/po/${po.id}/void`);
      setMessage(`PO ${po.po_number} ${received > 0 ? 'closed out' : 'voided'}`);
      setLikelyPOs(prev => prev.filter(p => p.id !== po.id));
    } catch (err) {
      console.error('Error voiding PO:', err);
      playError();
      setError(err.response?.data?.error || 'Failed to void PO');
    } finally {
      setPoActionId(null);
    }
  };

  // Handle barcode scan
  const handleScan = async (e) => {
    if (e) e.preventDefault();

    const code = scanInput.trim();
    if (!code) return;

    try {
      setLoading(true);
      setError(null);

      // Smart scan - lookup item and match to PO
      const response = await axios.post('/api/goods-receipt/scan', {
        scanCode: code,
        vendorId: selectedVendor?.id,
        poId: selectedPO?.id
      });

      if (response.data.success) {
        playSuccess();
        const { item, poLine, remainingQty, defaultQty } = response.data;

        // Open quantity editor
        setEditingItem({
          ...item,
          poLine,
          defaultQty,
          remainingQty,
          scanCode: code
        });
        setQuantityInput(defaultQty.toString());
        setUserStartedTyping(false);
        setShowKeypad(true);
      } else {
        playError();
        setError(response.data.error || 'Item not found on PO');
        // Auto-clear error after 3 seconds
        setTimeout(() => setError(null), 3000);
      }

      setScanInput('');
      // Keep focus on scan input
      setTimeout(() => {
        if (scanInputRef.current && !showKeypad) {
          scanInputRef.current.focus();
          scanInputRef.current.select();
        }
      }, 100);
    } catch (err) {
      playError();
      console.error('Error scanning item:', err);
      setError(err.response?.data?.error || 'Failed to scan item');
      setScanInput('');
      // Auto-clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
      // Keep focus on scan input
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
          scanInputRef.current.select();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  // Receive item with quantity
  const receiveItem = async () => {
    if (!editingItem || !quantityInput) return;

    const quantity = parseFloat(quantityInput);
    if (isNaN(quantity) || quantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    // Store the receive data
    const receiveData = {
      po_line_item_id: editingItem.poLine.id,
      line_number: editingItem.poLine.line_number,
      po_id: selectedPO.id,
      item_sku: editingItem.sku || editingItem.poLine.item_sku,
      item_description: editingItem.display_name || editingItem.poLine.item_description,
      vendor_item_code: editingItem.scanCode,
      quantity_received: quantity,
      unit_of_measure: editingItem.poLine.unit_of_measure || 'EA',
      condition: 'good'
    };

    setPendingReceiveData(receiveData);
    setLabelItem(editingItem);
    setShowKeypad(false);

    // In batch receive mode, skip the label prompt and auto-complete
    if (receiveQueue.length > 0) {
      // Directly complete the receive with auto-print setting
      setLabelQuantity(defaultLabelCount);
      // Use a slight delay to ensure state is set before completing
      setTimeout(() => {
        completeReceiveItem(autoPrintEnabled);
      }, 50);
    } else {
      // Single item receive - show label prompt for user confirmation
      setLabelQuantity(defaultLabelCount);
      setShowLabelPrompt(true);
    }
  };

  // Complete receiving item after label prompt
  const completeReceiveItem = async (printLabels) => {
    if (!pendingReceiveData) return;

    try {
      setLoading(true);

      // Add line item to receipt
      await axios.post(`/api/goods-receipt/receipt/${currentReceipt.id}/items`, pendingReceiveData);

      // Add to received items list
      setReceivedItems(prev => [...prev, {
        item: labelItem,
        quantity: pendingReceiveData.quantity_received,
        timestamp: new Date()
      }]);

      playSuccess();
      setMessage(`Received ${pendingReceiveData.quantity_received} × ${labelItem.display_name || labelItem.sku}`);

      // Print labels if requested
      if (printLabels && labelQuantity > 0) {
        // Prepare item data for printing
        const itemWithVendor = {
          ...labelItem,
          vendor_name: selectedPO.vendor_name,
          item_id: labelItem.poLine?.item_id || labelItem.item_id,
          item_sku: labelItem.sku || labelItem.poLine?.item_sku,
          poLine: {
            ...labelItem.poLine,
            vendor_name: selectedPO.vendor_name,
            item_id: labelItem.poLine?.item_id,
            item_sku: labelItem.poLine?.item_sku
          }
        };

        // Try ZPL printing first
        const zplPrinted = await printZPLLabels(
          itemWithVendor,
          selectedPO.po_number,
          new Date(),
          labelQuantity,
          selectedPO.netsuite_internal_id
        );

        if (!zplPrinted) {
          // Only fallback to browser print if explicitly set to browser method
          if (printMethod === 'browser') {
            setPrintData({
              item: itemWithVendor,
              poNumber: selectedPO.po_number,
              receivedDate: new Date(),
              quantity: labelQuantity
            });
            setReadyToPrint(true);
          } else {
            // For USB/network/download methods, just show error but still reset
            setError('Printing failed. Please check printer connection.');
            setTimeout(() => setError(null), 3000);
            resetAfterPrint();
          }
        } else {
          // ZPL print successful, reset immediately
          resetAfterPrint();
        }
      } else {
        // Reset and check for more items in queue
        setShowLabelPrompt(false);
        setPendingReceiveData(null);
        setLabelItem(null);
        setError(null);

        // If more items in queue, process next
        if (receiveQueue.length > 1) {
          continueToNextItem();
        } else {
          setEditingItem(null);
          setQuantityInput('');
          setSelectedLineItems([]);
          setReceiveQueue([]);
          // Focus back on scan input
          if (scanInputRef.current) {
            scanInputRef.current.focus();
          }
        }
      }

      // Check if PO is complete
      checkPOCompletion();
    } catch (err) {
      playError();
      console.error('Error receiving item:', err);
      setError('Failed to receive item');
    } finally {
      setLoading(false);
    }
  };

  // Check if all items on PO have been received
  const checkPOCompletion = async () => {
    try {
      // Reload PO to get updated quantities
      const response = await axios.get(`/api/goods-receipt/po/${selectedPO.po_number}`);
      const updatedPO = response.data;

      const allReceived = updatedPO.line_items.every(
        line => parseFloat(line.received_quantity) >= parseFloat(line.ordered_quantity)
      );

      if (allReceived) {
        playComplete();
        setMessage('All items received! PO is complete.');
      }
    } catch (err) {
      console.error('Error checking PO completion:', err);
    }
  };

  // Toggle item selection (checkbox mode)
  const toggleItemSelection = (lineItem) => {
    // Use line_number as unique identifier (id may not exist in cached data)
    const lineKey = lineItem.line_number || lineItem.id;
    const isSelected = selectedLineItems.find(item => (item.line_number || item.id) === lineKey);
    if (isSelected) {
      setSelectedLineItems(prev => prev.filter(item => (item.line_number || item.id) !== lineKey));
    } else {
      setSelectedLineItems(prev => [...prev, lineItem]);
    }
  };

  // Start batch receive process for selected items
  const startBatchReceive = () => {
    if (selectedLineItems.length === 0) return;

    // Create queue from selected items
    const queue = selectedLineItems.map(lineItem => ({
      sku: lineItem.item_sku,
      display_name: lineItem.item_description,
      poLine: lineItem,
      defaultQty: parseFloat(lineItem.ordered_quantity) - parseFloat(lineItem.received_quantity),
      remainingQty: parseFloat(lineItem.ordered_quantity) - parseFloat(lineItem.received_quantity)
    }));

    setReceiveQueue(queue);
    processNextInQueue(queue);
  };

  // Process next item in receive queue
  const processNextInQueue = (queue = receiveQueue) => {
    if (queue.length === 0) {
      // All done
      setSelectedLineItems([]);
      setReceiveQueue([]);
      return;
    }

    const nextItem = queue[0];
    setEditingItem(nextItem);
    setQuantityInput(nextItem.defaultQty.toString());
    setUserStartedTyping(false);
    setShowKeypad(true);
  };

  // Move to next item after receiving current one
  const continueToNextItem = () => {
    const remaining = receiveQueue.slice(1);
    setReceiveQueue(remaining);
    processNextInQueue(remaining);
  };

  // Complete receiving
  const completeReceiving = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/goods-receipt/receipt/${currentReceipt.id}/complete`);
      playComplete();
      setMode('completed');

      // Show success message with NetSuite Item Receipt number if available.
      // A native receipt (NetSuite unreachable) has none — say so rather than
      // letting it read like a normal completion.
      const itemReceiptNumber = response.data?.netsuite_item_receipt_number;
      const successMsg = itemReceiptNumber
        ? `Goods Receipt ${currentReceipt.receipt_number} completed! NetSuite Item Receipt: ${itemReceiptNumber}`
        : response.data?.native
          ? `Receipt ${currentReceipt.receipt_number} completed natively — stock and the PO are updated, but NetSuite is unreachable and has NO item receipt for it.`
          : `Receipt ${currentReceipt.receipt_number} completed!`;

      setMessage(successMsg);
    } catch (err) {
      console.error('Error completing receipt:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to complete receipt');
    } finally {
      setLoading(false);
    }
  };

  // Start over
  const resetReceiving = () => {
    setMode('po-selection');
    setSelectedVendor(null);
    setSelectedPO(null);
    setCurrentReceipt(null);
    setReceivedItems([]);
    setScanInput('');
    setVendorSearch('');
    setSearchResults([]);
    setError(null);
    setMessage(null);
    setEditingItem(null);
    setQuantityInput('');
    setShowKeypad(false);
    loadLikelyPOs();
  };

  // Keypad buttons
  const keypadButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '←'];

  const handleKeypadPress = (button) => {
    if (button === '←') {
      setQuantityInput(prev => prev.slice(0, -1));
      setUserStartedTyping(true);
    } else if (button === '.') {
      if (!quantityInput.includes('.')) {
        // If user hasn't started typing yet, overwrite default value
        if (!userStartedTyping) {
          setQuantityInput('.');
          setUserStartedTyping(true);
        } else {
          setQuantityInput(prev => prev + '.');
        }
      }
    } else {
      // If user hasn't started typing yet, overwrite default value
      if (!userStartedTyping) {
        setQuantityInput(button);
        setUserStartedTyping(true);
      } else {
        setQuantityInput(prev => prev + button);
      }
    }
  };

  // Render PO selection mode
  // Estimated arrival as a Date (or null) so the list can sort on it; display
  // formatting is a thin wrapper around this.
  const getEstimatedArrivalDate = (po) => {
    // Priority 1: firmed ship date
    if (po.firmed_ship_date) return new Date(po.firmed_ship_date);
    // Priority 2: due date (expected_delivery_date)
    if (po.expected_delivery_date) return new Date(po.expected_delivery_date);
    // Priority 3: calculate from order date + lead time
    if (po.order_date && po.line_items && po.line_items.length > 0) {
      const maxLeadTime = Math.max(...po.line_items.map(item => item.item_leadtime || 0));
      if (maxLeadTime > 0) {
        const estimatedDate = new Date(po.order_date);
        estimatedDate.setDate(estimatedDate.getDate() + maxLeadTime);
        return estimatedDate;
      }
    }
    return null;
  };
  const getEstimatedArrival = (po) => {
    const d = getEstimatedArrivalDate(po);
    return d ? d.toLocaleDateString() : 'TBD';
  };

  // POs destined for the Candlefish Charleston store are booked in NetSuite
  // under the Rewined subsidiary (Rewined manufactures for both brands) with
  // "Candlefish Charleston" as the vendor — so subsidiary alone misses them.
  // Vendor name is the reliable signal for "which brand does this belong to."
  const getPOBrand = (po) => {
    if (/candlefish/i.test(po.vendor_name || '')) return 'Candlefish';
    return po.subsidiary || 'Rewined';
  };

  // Received/ordered/pending rollup across a PO's lines
  const getPOProgress = (po) => {
    const items = po.line_items || [];
    const ordered = items.reduce((s, i) => s + (parseFloat(i.ordered_quantity) || 0), 0);
    const received = items.reduce((s, i) => s + (parseFloat(i.received_quantity) || 0), 0);
    const pending = items.filter(i => (parseFloat(i.received_quantity) || 0) < (parseFloat(i.ordered_quantity) || 0)).length;
    return { ordered, received, pending };
  };

  const POSORT_COLUMNS = [
    { key: 'vendor', label: 'Vendor' },
    { key: 'subsidiary', label: 'Brand' },
    { key: 'po_number', label: 'PO #' },
    { key: 'order_date', label: 'PO Date' },
    { key: 'arrival', label: 'Est. Arrival' },
    { key: 'pending', label: 'Pending Items' },
    { key: 'progress', label: 'Received' },
  ];

  const togglePoSort = (key) => {
    setPoSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const filteredSortedPOs = (() => {
    const bySubsidiary = subsidiaryFilter === 'all'
      ? likelyPOs
      : likelyPOs.filter(po => getPOBrand(po) === subsidiaryFilter);

    const q = poFilter.trim().toLowerCase();
    const filtered = !q ? bySubsidiary : bySubsidiary.filter(po => {
      if ((po.vendor_name || '').toLowerCase().includes(q)) return true;
      if ((po.po_number || '').toLowerCase().includes(q)) return true;
      if (getPOBrand(po).toLowerCase().includes(q)) return true;
      return (po.line_items || []).some(item =>
        (item.item_sku || '').toLowerCase().includes(q) ||
        (item.item_description || '').toLowerCase().includes(q) ||
        (item.vendor_item_code || '').toLowerCase().includes(q)
      );
    });

    const mult = poSort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (poSort.key) {
        case 'vendor':
          return mult * (a.vendor_name || '').localeCompare(b.vendor_name || '');
        case 'subsidiary':
          return mult * getPOBrand(a).localeCompare(getPOBrand(b));
        case 'po_number':
          return mult * (a.po_number || '').localeCompare(b.po_number || '');
        case 'order_date':
          return mult * (new Date(a.order_date || 0) - new Date(b.order_date || 0));
        case 'pending':
          return mult * (getPOProgress(a).pending - getPOProgress(b).pending);
        case 'progress': {
          const pa = getPOProgress(a), pb = getPOProgress(b);
          const ra = pa.ordered > 0 ? pa.received / pa.ordered : 0;
          const rb = pb.ordered > 0 ? pb.received / pb.ordered : 0;
          return mult * (ra - rb);
        }
        case 'arrival':
        default: {
          const da = getEstimatedArrivalDate(a);
          const db = getEstimatedArrivalDate(b);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return mult * (da - db);
        }
      }
    });
  })();

  const renderPOSelection = () => (
    <div className="po-selection">
      {/* Open POs — searchable, sortable list (both Rewined and Candlefish) */}
      <div className="likely-pos-section">
        <div className="po-list-toolbar">
          <div className="subsidiary-toggle">
            {['all', 'Rewined', 'Candlefish'].map(opt => (
              <button
                key={opt}
                type="button"
                className={`subsidiary-toggle-btn ${subsidiaryFilter === opt ? 'active' : ''}`}
                onClick={() => setSubsidiaryFilter(opt)}
              >
                {opt === 'all' ? 'All' : opt}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="po-list-filter"
            placeholder="Filter by vendor, PO #, brand, or item SKU..."
            value={poFilter}
            onChange={(e) => setPoFilter(e.target.value)}
          />
          <span className="po-list-count">{filteredSortedPOs.length} open PO{filteredSortedPOs.length === 1 ? '' : 's'}</span>
        </div>
        {filteredSortedPOs.length === 0 ? (
          <p className="no-pos">No open purchase orders</p>
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead>
                <tr>
                  {POSORT_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => togglePoSort(col.key)}
                      className={poSort.key === col.key ? 'po-table-th-active' : ''}
                    >
                      {col.label}
                      {poSort.key === col.key && (poSort.dir === 'asc' ? ' ↑' : ' ↓')}
                    </th>
                  ))}
                  <th className="po-table-actions-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedPOs.map(po => {
                  const progress = getPOProgress(po);
                  const pct = progress.ordered > 0 ? Math.round((progress.received / progress.ordered) * 100) : 0;
                  return (
                    <tr key={po.id} onClick={() => selectPO(po)}>
                      <td>{po.vendor_name}</td>
                      <td>
                        <span className={`subsidiary-badge ${getPOBrand(po).toLowerCase()}`}>
                          {getPOBrand(po)}
                        </span>
                      </td>
                      <td className="po-table-po-number">{po.po_number}</td>
                      <td>{po.order_date ? new Date(po.order_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{getEstimatedArrival(po)}</td>
                      <td>{progress.pending}</td>
                      <td>
                        <div className="po-progress-bar">
                          <div className="po-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="po-progress-label">{progress.received} / {progress.ordered}</span>
                      </td>
                      <td className="po-table-actions-td" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="po-action-btn receive-all"
                          disabled={poActionId === po.id}
                          onClick={() => handleReceiveAll(po)}
                        >
                          {poActionId === po.id ? '…' : 'Receive All'}
                        </button>
                        <button
                          type="button"
                          className="po-action-btn void"
                          disabled={poActionId === po.id}
                          onClick={() => handleVoidPO(po)}
                          title={progress.received > 0 ? "Close out what's left — never coming" : 'Nothing on this PO was ever received'}
                        >
                          {poActionId === po.id ? '…' : (progress.received > 0 ? 'Close Remaining' : 'Void')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Search PO */}
      <div className="vendor-search-section">
        <h3>Or Search by Item, Vendor, or PO Number</h3>
        <div className="search-with-keyboard">
          <input
            ref={vendorSearchRef}
            type="text"
            className="vendor-search-input"
            placeholder="Search by item SKU, vendor name, PO#, or vendor item code..."
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            autoComplete="off"
            readOnly
            onClick={() => {
              setShowSearchKeypad(true);
              setSearchKeypadValue(vendorSearch);
            }}
          />
          <button
            className="keyboard-button"
            onClick={() => {
              setShowSearchKeypad(true);
              setSearchKeypadValue(vendorSearch);
            }}
            type="button"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="5" y1="9" x2="7" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="9" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="13" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="17" y1="9" x2="19" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="5" y1="12" x2="7" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="9" y1="12" x2="11" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="13" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="17" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="7" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <AnimatePresence>
          {searchResults.length > 0 && (
            <motion.div
              className="search-results"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {searchResults.map(po => (
                <div
                  key={po.id}
                  className="search-result-item"
                  onClick={() => selectPO(po)}
                >
                  <div className="po-search-result">
                    <span className="po-number">{po.po_number}</span>
                    <span className="vendor-name">{po.vendor_name}</span>
                  </div>
                  <div className="po-date-info">
                    {po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : 'No date'}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Search Keypad Modal */}
      <AnimatePresence>
        {showSearchKeypad && (
          <motion.div
            className="keypad-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSearchKeypad(false)}
          >
            <motion.div
              className="keypad-modal search-keypad"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Search</h3>
              <div className="quantity-display">{searchKeypadValue || 'Type to search...'}</div>

              <div className="keypad-grid alpha-keypad">
                {['1','2','3','4','5','6','7','8','9','0',
                  'Q','W','E','R','T','Y','U','I','O','P',
                  'A','S','D','F','G','H','J','K','L','-',
                  'Z','X','C','V','B','N','M','←','SPACE'].map((key, idx) => (
                  <button
                    key={idx}
                    className={`keypad-button ${key === '←' ? 'backspace' : ''} ${key === 'SPACE' ? 'space-key' : ''}`}
                    onClick={() => {
                      if (key === '←') {
                        setSearchKeypadValue(searchKeypadValue.slice(0, -1));
                      } else if (key === 'SPACE') {
                        setSearchKeypadValue(searchKeypadValue + ' ');
                      } else {
                        setSearchKeypadValue(searchKeypadValue + key);
                      }
                    }}
                  >
                    {key === 'SPACE' ? '␣' : key}
                  </button>
                ))}
              </div>

              <div className="keypad-actions">
                <button
                  className="keypad-cancel"
                  onClick={() => {
                    setShowSearchKeypad(false);
                    setSearchKeypadValue('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="keypad-confirm"
                  onClick={() => {
                    setVendorSearch(searchKeypadValue);
                    setShowSearchKeypad(false);
                  }}
                >
                  Search
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Render receiving mode
  const renderReceiving = () => (
    <div className="receiving-mode">
      {/* PO Line Items with checkboxes */}
      <div className="po-items-list">
        <div className="items-header">
          <h3>Items to Receive</h3>
          {selectedLineItems.length > 0 && (
            <button className="receive-selected-btn" onClick={startBatchReceive}>
              Receive {selectedLineItems.length} Selected
            </button>
          )}
        </div>
        {selectedPO?.line_items?.map(lineItem => {
          const baseReceived = parseFloat(lineItem.received_quantity) || 0;
          const ordered = parseFloat(lineItem.ordered_quantity);

          // Add quantities received in this session
          const sessionReceived = receivedItems
            .filter(ri => ri.item.poLine?.id === lineItem.id || ri.item.poLine?.line_number === lineItem.line_number)
            .reduce((sum, ri) => sum + ri.quantity, 0);

          const totalReceived = baseReceived + sessionReceived;
          const remaining = ordered - totalReceived;
          const isComplete = remaining <= 0;
          const isReceived = receivedItems.find(ri => ri.item.poLine?.id === lineItem.id || ri.item.poLine?.line_number === lineItem.line_number);
          const isSelected = selectedLineItems.find(item => item.id === lineItem.id || item.line_number === lineItem.line_number);

          return (
            <motion.div
              key={lineItem.id || lineItem.line_number}
              className={`po-item ${isComplete ? 'complete' : ''} ${isReceived ? 'just-received' : ''} ${isSelected ? 'selected' : ''}`}
              whileHover={{ scale: isComplete ? 1 : 1.01 }}
              onClick={() => !isComplete && !isReceived && toggleItemSelection(lineItem)}
            >
              <div className="item-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isComplete || !!isReceived || !!isSelected}
                  onChange={() => !isComplete && !isReceived && toggleItemSelection(lineItem)}
                  disabled={isComplete || !!isReceived}
                />
              </div>
              <div className="item-details">
                <div className="item-sku">{lineItem.item_sku}</div>
                <div className="item-description">{lineItem.item_description}</div>
                {lineItem.vendor_item_code && (
                  <div className="vendor-item-code">Vendor Code: {lineItem.vendor_item_code}</div>
                )}
              </div>
              <div className="item-quantity">
                <span className={remaining === 0 ? 'complete' : ''}>
                  {totalReceived} / {ordered}
                </span>
                {remaining > 0 && <span className="remaining"> ({remaining} remaining)</span>}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Recently received items */}
      {receivedItems.length > 0 && (
        <div className="received-items-list">
          <h3>Recently Received</h3>
          {receivedItems.slice().reverse().slice(0, 5).map((received, idx) => (
            <motion.div
              key={idx}
              className="received-item"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="item-name">{received.item.display_name || received.item.sku}</span>
              <span className="item-qty">× {received.quantity}</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* Submit button */}
      {receivedItems.length > 0 && (
        <div className="submit-section">
          <button className="submit-receipt-button" onClick={completeReceiving}>
            Complete Receipt & Submit to NetSuite
          </button>
        </div>
      )}

      {/* Scan input at bottom */}
      <form onSubmit={handleScan} className="scan-form-bottom">
        <input
          ref={scanInputRef}
          type="text"
          className="scan-input"
          placeholder="Scan item code or vendor item code..."
          value={scanInput}
          onChange={(e) => setScanInput(e.target.value)}
          autoComplete="off"
          disabled={loading}
          autoFocus
        />
      </form>
    </div>
  );

  // Render completed mode
  const renderCompleted = () => (
    <div className="completed-mode">
      <motion.div
        className="success-icon"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200 }}
      >
        ✓
      </motion.div>
      <h2>Receipt Completed!</h2>
      <div className="receipt-info">
        <p>Receipt Number: <strong>{currentReceipt?.receipt_number}</strong></p>
        <p>PO: <strong>{selectedPO?.po_number}</strong></p>
        <p>Items Received: <strong>{receivedItems.length}</strong></p>
      </div>
      <button className="start-over-button" onClick={resetReceiving}>
        Receive Another PO
      </button>
    </div>
  );

  // PIN entry keypad buttons
  const pinKeypadButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '←'];

  // If not logged in, show PIN entry
  if (showPinEntry) {
    return (
      <div className="goods-receipt-page">
        <div className="pin-entry-container">
          <h1>Goods Receipt</h1>
          <h2>Enter Your PIN</h2>

          <div className="pin-entry-box">
            <div className="pin-display">
              {[0, 1, 2, 3].map((idx) => (
                <div key={idx} className={`pin-dot ${pinInput.length > idx ? 'filled' : ''}`} />
              ))}
            </div>

            {pinError && <div className="pin-error">{pinError}</div>}

            <div className="pin-keypad">
              {pinKeypadButtons.map((btn) => (
                <button
                  key={btn}
                  className={`pin-key ${btn === 'C' ? 'clear' : ''} ${btn === '←' ? 'backspace' : ''}`}
                  onClick={() => handlePinKeypadPress(btn)}
                >
                  {btn}
                </button>
              ))}
            </div>

            <button
              className="pin-submit"
              onClick={verifyPin}
              disabled={pinInput.length !== 4}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="goods-receipt-page">
      {mode !== 'po-selection' && (
        <div className="page-header">
          <h1>Goods Receipt</h1>
          <div className="header-controls">
            {mode === 'receiving' && (
              <div className="auto-print-settings">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={autoPrintEnabled}
                    onChange={(e) => setAutoPrintEnabled(e.target.checked)}
                  />
                  <span>Auto-Print Labels</span>
                </label>

                {/* QZ Tray Status */}
                <span
                  className={`station-status ${qzConnected ? 'online' : 'offline'}`}
                  title={qzConnected ? `QZ Tray connected - ${qzSelectedPrinter || 'Auto-detect'}` : 'QZ Tray not connected'}
                >
                  {qzConnected ? '●' : '○'} {qzSelectedPrinter ? qzSelectedPrinter.split(' ')[0] : 'QZ'}
                </span>
                <button
                  className="test-server-btn"
                  onClick={async () => {
                    try {
                      if (!qzConnected) {
                        await connectToQZ();
                      }
                      await qzPrint.printTestLabel(qzSelectedPrinter || null);
                      setMessage('Test label sent!');
                      setTimeout(() => setMessage(null), 3000);
                    } catch (err) {
                      setError(`Test print failed: ${err.message}`);
                      setTimeout(() => setError(null), 5000);
                    }
                  }}
                  title="Print test label"
                >
                  Test
                </button>

              </div>
            )}
            {currentOperator && (
              <div className="operator-info">
                <span className="operator-name">{getOperatorName()}</span>
                <button className="logout-button" onClick={logoutOperator}>
                  Logout
                </button>
              </div>
            )}
            {mode !== 'completed' && (
              <button className="cancel-button" onClick={resetReceiving}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error/Success messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            className="message error"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {error}
            <button onClick={() => setError(null)}>×</button>
          </motion.div>
        )}
        {message && (
          <motion.div
            className="message success"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {message}
            <button onClick={() => setMessage(null)}>×</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <AnimatePresence mode="wait">
        {loading && (
          <motion.div
            className="loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="loading-spinner" />
          </motion.div>
        )}

        {mode === 'po-selection' && (
          <motion.div
            key="po-selection"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {renderPOSelection()}
          </motion.div>
        )}

        {mode === 'receiving' && (
          <motion.div
            key="receiving"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {renderReceiving()}
          </motion.div>
        )}

        {mode === 'completed' && (
          <motion.div
            key="completed"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            {renderCompleted()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* On-screen keypad modal */}
      <AnimatePresence>
        {showKeypad && editingItem && (
          <motion.div
            className="keypad-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowKeypad(false)}
          >
            <motion.div
              className="keypad-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>
                Enter Quantity
                {receiveQueue.length > 1 && (
                  <span className="batch-progress">
                    {' '}({selectedLineItems.length - receiveQueue.length + 1} of {selectedLineItems.length})
                  </span>
                )}
              </h3>
              <div className="item-info">
                <div className="item-name">{editingItem.display_name || editingItem.sku}</div>
                <div className="item-sku-small">{editingItem.sku || editingItem.poLine?.item_sku}</div>
              </div>

              <div className="quantity-display">{quantityInput || '0'}</div>

              <div className="keypad-grid">
                {keypadButtons.map((button, idx) => (
                  <button
                    key={idx}
                    className={`keypad-button ${button === '←' ? 'backspace' : ''}`}
                    onClick={() => handleKeypadPress(button)}
                  >
                    {button}
                  </button>
                ))}
              </div>

              <div className="keypad-actions">
                <button
                  className="keypad-cancel"
                  onClick={() => {
                    setShowKeypad(false);
                    setEditingItem(null);
                    setQuantityInput('');
                    setReceiveQueue([]);
                    setSelectedLineItems([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="keypad-confirm"
                  onClick={receiveItem}
                  disabled={!quantityInput || parseFloat(quantityInput) <= 0}
                >
                  Receive
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Label Printing Prompt Modal */}
      <AnimatePresence>
        {showLabelPrompt && labelItem && (
          <motion.div
            className="keypad-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="keypad-modal label-prompt-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Print Labels?</h3>
              <div className="label-prompt-info">
                <div className="label-prompt-item">{labelItem.display_name || labelItem.sku}</div>
                <div className="label-prompt-qty">Qty Received: {pendingReceiveData?.quantity_received}</div>
              </div>

              <div className="label-quantity-section">
                <div className="label-quantity-display">
                  <button
                    className="label-qty-btn"
                    onClick={() => setLabelQuantity(Math.max(1, labelQuantity - 1))}
                  >
                    ←
                  </button>
                  <div className="label-qty-number">{labelQuantity}</div>
                  <button
                    className="label-qty-btn"
                    onClick={() => setLabelQuantity(labelQuantity + 1)}
                  >
                    →
                  </button>
                </div>
                <div className="label-qty-text">Number of labels to print</div>
              </div>

              <div className="keypad-actions">
                <button
                  className="keypad-cancel"
                  onClick={() => completeReceiveItem(false)}
                >
                  Skip Printing
                </button>
                <button
                  className="keypad-confirm"
                  onClick={() => completeReceiveItem(autoPrintEnabled)}
                >
                  {autoPrintEnabled ? 'Confirm & Print' : 'Print'} {labelQuantity} Label{labelQuantity > 1 ? 's' : ''}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Print Component - Only render when we have real data */}
      {printData && (
        <ReceivingLabelPrint
          ref={labelPrintRef}
          item={printData.item}
          poNumber={printData.poNumber}
          receivedDate={printData.receivedDate}
          quantity={printData.quantity}
        />
      )}

      {/* Printer Diagnostics Modal */}
      <AnimatePresence>
        {showDiagnostics && (
          <motion.div
            className="diagnostic-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDiagnostics(false)}
          >
            <motion.div
              className="diagnostic-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="diagnostic-header">
                <h2>🔍 Printer Diagnostics</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowDiagnostics(false)}
                >
                  ×
                </button>
              </div>

              <div className="diagnostic-content">
                {!diagnosticResults ? (
                  <div className="diagnostic-start">
                    <p>Run diagnostics to check your printer setup and connectivity.</p>
                    <button
                      className="run-diagnostic-btn"
                      onClick={runDiagnostics}
                    >
                      Run Diagnostics
                    </button>
                  </div>
                ) : (
                  <div className="diagnostic-results">
                    {/* System Info */}
                    <div className="diagnostic-section">
                      <h3>📋 System Information</h3>
                      <div className="info-grid">
                        {diagnosticResults.printMethod === 'local' ? (
                          <>
                            <div>Print Method: <strong>Local Server</strong></div>
                            <div>Server Status: <strong>{diagnosticResults.localServerStatus ? '✅ Running' : '❌ Not Running'}</strong></div>
                            <div>Server URL: <strong>{localPrintServer}</strong></div>
                            <div>Printers Found: <strong>{diagnosticResults.printers.length}</strong></div>
                          </>
                        ) : (
                          <>
                            <div>Total Printers: <strong>{diagnosticResults.printers.length}</strong></div>
                            <div>USB Printers: <strong>{diagnosticResults.usbPrinters.length}</strong></div>
                            <div>Zebra Printers: <strong>{diagnosticResults.zebraPrinters.length}</strong></div>
                            <div>USB001 Port: <strong>{diagnosticResults.usb001 ? '✅ Connected' : '❌ Not Found'}</strong></div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Printer List */}
                    {diagnosticResults.printers.length > 0 && (
                      <div className="diagnostic-section">
                        <h3>🖨️ Detected Printers</h3>
                        <div className="printer-list">
                          {diagnosticResults.printers.map((printer, idx) => (
                            <div key={idx} className="printer-item">
                              <div className="printer-name">
                                {printer.isZebra && '✅ '}{printer.name}
                              </div>
                              <div className="printer-details">
                                Port: {printer.port} | Driver: {printer.driver}
                                {printer.isDefault && ' | Default'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    <div className="diagnostic-section">
                      <h3>💡 Recommendations</h3>
                      {diagnosticResults.recommendations.map((rec, idx) => (
                        <div
                          key={idx}
                          className={`recommendation ${rec.type}`}
                        >
                          {rec.type === 'success' && '✅ '}
                          {rec.type === 'warning' && '⚠️ '}
                          {rec.type === 'error' && '❌ '}
                          {rec.text}
                        </div>
                      ))}
                    </div>

                    {/* Test Print */}
                    <div className="diagnostic-section">
                      <h3>🧪 Test Print</h3>
                      {!testPrintStatus ? (
                        <div>
                          <p>Send a test label to verify printer connectivity.</p>
                          <button
                            className="test-print-btn"
                            onClick={sendTestPrint}
                            disabled={diagnosticResults.printers.length === 0}
                          >
                            Send Test Label
                          </button>
                        </div>
                      ) : (
                        <div className={`test-result ${testPrintStatus.success ? 'success' : 'error'}`}>
                          {testPrintStatus.loading && '⏳ '}
                          {testPrintStatus.success && '✅ '}
                          {!testPrintStatus.loading && !testPrintStatus.success && '❌ '}
                          {testPrintStatus.message}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="diagnostic-actions">
                      <button
                        className="rerun-btn"
                        onClick={runDiagnostics}
                      >
                        Re-run Diagnostics
                      </button>
                      <button
                        className="close-diagnostic-btn"
                        onClick={() => setShowDiagnostics(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GoodsReceipt;
